import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const B2BTopupSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [topupData, setTopupData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('agent_token');
    if (!token) {
      navigate('/b2b-agent');
      return;
    }
    
    const topupId = searchParams.get('topup_id');
    if (topupId) {
      confirmTopup(topupId);
    } else {
      setLoading(false);
      toast.error('Invalid top-up reference');
    }
  }, [searchParams, navigate]);

  const confirmTopup = async (topupId) => {
    try {
      const response = await axios.get(
        `${API}/b2b/wallet/topup-confirm/${topupId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('agent_token')}` } }
      );
      
      setTopupData(response.data);
      setSuccess(true);
      toast.success('Wallet top-up successful!');
    } catch (error) {
      console.error('Top-up confirmation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm top-up');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#1A4D7F] mx-auto mb-4" />
          <p className="text-slate-600">Processing your top-up...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="topup-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full"
      >
        <Card className="border-slate-200 shadow-xl">
          <CardContent className="p-8 text-center">
            {success ? (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </motion.div>

                <h1 className="text-3xl font-heading font-bold text-[#1A4D7F] mb-3">
                  Top-up Successful!
                </h1>
                <p className="text-slate-600 mb-6">
                  Your wallet has been credited successfully.
                </p>

                {topupData && (
                  <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Amount Added:</span>
                      <span className="font-bold text-green-600">+{topupData.amount?.toFixed(2)} AED</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">New Balance:</span>
                      <span className="font-bold text-[#1A4D7F]">{topupData.new_balance?.toFixed(2)} AED</span>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/b2b-agent/dashboard')}
                    className="w-full bg-[#1A4D7F] hover:bg-[#153e66]"
                  >
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    onClick={() => navigate('/b2b-agent/apply')}
                    variant="outline"
                    className="w-full"
                  >
                    New Visa Application
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">❌</span>
                </div>
                <h1 className="text-2xl font-heading font-bold text-slate-800 mb-3">
                  Top-up Failed
                </h1>
                <p className="text-slate-600 mb-6">
                  There was an issue processing your top-up. Please try again.
                </p>
                <Button
                  onClick={() => navigate('/b2b-agent/add-credit')}
                  className="w-full bg-[#1A4D7F]"
                >
                  Try Again
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default B2BTopupSuccess;
